/**
 * Layout wrapper for project pages: real-time subscription, announcements,
 * and the calendar/task modals its screens share. The project's header
 * (name, favorite, settings, search, members) is ProjectTopbar, which
 * AppLayout puts in the app's topbar.
 */

import { useState, useEffect } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { EventModal } from '@/components/modals/EventModal';
import { TaskDetailModal } from '@/components/modals/TaskDetailModal';
import { AnnouncementBanner } from '@/components/ui/AnnouncementBanner';
import { useSocketStore } from '@/stores/socket.store';
import { useAuthStore } from '@/stores/auth.store';
import { useProjectRoles } from '@/hooks/useProjectRoles';
import { projectsApi, tasksApi, announcementsApi, CalendarEvent, CalendarTask } from '@/lib/api';

export function ProjectLayout() {
  const { projectId } = useParams<{ projectId: string }>();
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventInitialDate, setEventInitialDate] = useState<Date | undefined>(undefined);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [selectedTask, setSelectedTask] = useState<CalendarTask | null>(null);
  const { user } = useAuthStore();

  // Get project details
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId,
  });

  // Fetch task lists for this project (needed for CreateTaskModal)
  const { data: taskLists } = useQuery({
    queryKey: ['taskLists', projectId],
    queryFn: () => tasksApi.listTaskLists(projectId!),
    enabled: !!projectId,
  });

  // Fetch active announcements for this project
  const { data: announcements } = useQuery({
    queryKey: ['announcements', 'project', projectId],
    queryFn: () => announcementsApi.getActiveForProject(projectId!),
    enabled: !!projectId,
    staleTime: 30000,
    refetchInterval: 60000,
  });

  // Get socket subscription methods
  const { subscribeToProject, unsubscribeFromProject } = useSocketStore();

  // Subscribe to project room for real-time updates (e.g., AI summary updates)
  useEffect(() => {
    if (projectId) {
      subscribeToProject(projectId);
      return () => {
        unsubscribeFromProject(projectId);
      };
    }
  }, [projectId, subscribeToProject, unsubscribeFromProject]);

  // Handlers for calendar events
  const handleAddEvent = (date?: Date) => {
    setEventInitialDate(date);
    setEditingEvent(null);
    setShowEventModal(true);
  };

  // Staff / admin of THIS project: Solid anywhere, a partner agency on its own
  const { isProjectStaff: isAgencyMember, isProjectAdmin: isAgencyAdmin } = useProjectRoles(projectId);

  // Check if user can edit a specific event
  // Agency admins can edit any event, others can only edit events they created
  const canEditEvent = (event: CalendarEvent) => {
    if (isAgencyAdmin) return true;
    return event.createdBy?.id === user?.id;
  };

  const handleEventClick = (event: CalendarEvent) => {
    // Only open modal if user has permission
    if (canEditEvent(event)) {
      setEditingEvent(event);
      setEventInitialDate(undefined);
      setShowEventModal(true);
    }
  };

  const handleTaskClick = (task: CalendarTask) => {
    setSelectedTask(task);
  };

  const isArchived = !!project?.archivedAt;

  // Context to pass to child routes
  const outletContext = {
    project,
    onAddEvent: !isArchived ? handleAddEvent : undefined,
    onEventClick: handleEventClick,
    onTaskClick: handleTaskClick,
    canEditEvent: !isArchived ? canEditEvent : () => false,
    isAgencyMember,
    isAgencyAdmin,
    userId: user?.id,
  };

  return (
    <div>
      {/* The project's header is in the app's topbar (ProjectTopbar, via
          AppLayout) — one header, not a "Projects" bar over a second one.
          Announcements open the content instead. */}
      {announcements && announcements.length > 0 && (
        <div className="mb-6">
          <AnnouncementBanner announcements={announcements} />
        </div>
      )}

      {/* The shell's content column supplies the ground and padding. */}
      <div className="animate-fade-in">
        <Outlet context={outletContext} />
      </div>

      {/* Event Modal */}
      {projectId && (
        <EventModal
          isOpen={showEventModal}
          projectId={projectId}
          onClose={() => {
            setShowEventModal(false);
            setEventInitialDate(undefined);
            setEditingEvent(null);
          }}
          event={editingEvent}
          initialDate={eventInitialDate}
        />
      )}

      {/* Task Detail Modal */}
      {selectedTask && projectId && project && (
        <TaskDetailModal
          isOpen={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          task={{
            id: selectedTask.id,
            title: selectedTask.title,
            description: selectedTask.description,
            status: selectedTask.status,
            priority: selectedTask.priority,
            dueDate: selectedTask.dueDate,
            assignee: selectedTask.assignee,
            assigneeId: selectedTask.assigneeId,
            createdById: selectedTask.createdById,
            createdBy: selectedTask.createdBy,
          }}
          projectId={projectId}
          listId={selectedTask.list.id}
          organizationId={project.organizationId}
        />
      )}

    </div>
  );
}
